import React, { useCallback, useEffect, useState } from "react";
import { MonitorSmartphone, LogOut, Loader2, X } from "lucide-react";
import api from "../../api";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";

const ago = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

export const SessionsCard = () => {
  const { t } = useLang();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/my/sessions").then((r) => setData(r.data)).catch(() => setData({ sessions: [] }));
  }, []);
  useEffect(load, [load]);

  const revoke = async (jti) => {
    setBusy(jti);
    try { await api.delete(`/my/sessions/${jti}`); toast({ title: t("ses.signedOutDevice") }); load(); }
    catch { toast({ title: t("acc.pleaseTryAgain") }); }
    finally { setBusy(""); }
  };

  const revokeAll = async () => {
    setBusy("all");
    try {
      const { data: res } = await api.post("/my/sessions/revoke-all");
      if (res.token) localStorage.setItem("lp-token", res.token);
      toast({ title: t("ses.signedOutAll"), description: t("ses.signedOutAllNote") });
      load();
    } catch { toast({ title: t("acc.pleaseTryAgain") }); }
    finally { setBusy(""); }
  };

  if (!data) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="sessions-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-extrabold text-foreground flex items-center gap-1.5">
            <MonitorSmartphone size={16} className="text-[#16a34a]" /> {t("ses.title")}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {data.idleHours ? t("ses.idleNote", { n: data.idleHours }) : t("ses.sub")}
          </p>
        </div>
        <button onClick={revokeAll} disabled={busy === "all"} data-testid="sign-out-everywhere-btn"
          className="text-xs font-bold border border-border hover:bg-secondary rounded-xl px-4 py-2.5 transition-colors inline-flex items-center gap-1.5">
          {busy === "all" ? <Loader2 className="animate-spin" size={13} /> : <LogOut size={13} />} {t("ses.signOutAll")}
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {(data.sessions || []).map((s) => (
          <div key={s.jti} className="flex items-center justify-between gap-3 bg-secondary rounded-xl px-3 py-2.5" data-testid={`session-${s.jti}`}>
            <div className="min-w-0">
              <p className="text-sm font-bold text-foreground truncate">
                {s.label || "Device"}
                {s.current && <span className="ml-2 text-[10px] font-extrabold uppercase tracking-wide text-[#16a34a]">{t("ses.thisDevice")}</span>}
              </p>
              <p className="text-xs text-muted-foreground truncate">{ago(s.lastSeen)}{s.ip ? ` · ${s.ip}` : ""}</p>
            </div>
            {!s.current && (
              <button onClick={() => revoke(s.jti)} disabled={busy === s.jti} data-testid={`revoke-session-${s.jti}`}
                className="shrink-0 text-muted-foreground hover:text-rose-500 transition-colors p-1.5" aria-label={t("ses.signOutDevice")}>
                {busy === s.jti ? <Loader2 className="animate-spin" size={15} /> : <X size={15} />}
              </button>
            )}
          </div>
        ))}
        {!(data.sessions || []).length && (
          <p className="text-sm text-muted-foreground" data-testid="sessions-empty">{t("ses.empty")}</p>
        )}
      </div>
    </div>
  );
};

export default SessionsCard;
